import React, { useState, useRef, useEffect } from 'react';
import { NextUI } from '@/components/NextUI';
import { useTranslations } from 'next-intl';
import { truncateDocumentName } from '@/helpers/document-name';

const Rename = ({
  data,
}: {
  data: {
    initialFileName: string;
    rename: (newFileName: string) => void;
    close: () => void;
    maxNameLength?: number;
  };
}) => {
  const t = useTranslations('modals.rename');
  const [fileName, setFileName] = useState(data.initialFileName);
  const inputRef = useRef<HTMLInputElement>(null);
  const isValid =
    fileName.trim().length > 0 &&
    (data.maxNameLength === undefined ||
      Array.from(fileName).length <= data.maxNameLength);

  const handleRename = () => {
    if (!isValid) return;
    data.rename(fileName);
    data.close();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      handleRename();
    }
  };

  useEffect(() => {
    const inputElement = inputRef.current?.querySelector(
      'input'
    ) as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
      inputElement.select();
    }
  }, []);

  return (
    <NextUI.Modal
      className="max-w-[500px]"
      classNames={{
        content: 'p-8',
        closeButton: 'right-2 top-3 text-gray-medium',
      }}
      defaultOpen={true}
      name={'Rename'}
      backdrop="blur"
    >
      <h3 className="mb-4 text-center text-2xl font-bold text-gray-900">
        {t('title')}
      </h3>

      <div ref={inputRef}>
        <NextUI.Input
          classNames={{
            base: 'focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 !ring-transparent focus:!border-[3px]',
            innerWrapper:
              'focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 !ring-0 !ring-transparent ',
            inputWrapper:
              'focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 !ring-0 !ring-transparent ',
            input:
              'focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 !ring-0 !ring-transparent ',
            mainWrapper:
              'focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 !ring-0 !ring-transparent ',
          }}
          type="text"
          value={fileName}
          onChange={(e) =>
            setFileName(
              data.maxNameLength === undefined
                ? e.target.value
                : truncateDocumentName(e.target.value, data.maxNameLength)
            )
          }
          onKeyDown={handleKeyDown}
          variant="outline"
          radius="sm"
          placeholder={t('placeholder')}
          autoFocus
        />
      </div>

      <div className="flex flex-row gap-2 mt-5">
        <NextUI.Button
          className="flex-1 !min-w-0 !border-0 !bg-gray-medium2"
          variant="outline"
          onClick={() => data.close()}
        >
          {t('buttons.cancel')}
        </NextUI.Button>
        <NextUI.Button
          className="flex-1 !min-w-0"
          onClick={handleRename}
          disabled={!isValid}
        >
          {t('buttons.rename')}
        </NextUI.Button>
      </div>
    </NextUI.Modal>
  );
};

export default Rename;
